import { TransactionDrawer } from '../../../atoms/TransactionDrawer/TransactionDrawer';
import { TransactionItemsList } from '../../../atoms/TransactionItemsList/TransactionItemsList';
import { SubinventoryQtyBox } from '../../../atoms/SubinventoryQtyBox/SubinventoryQtyBox';
import { useAppContext } from '../../../../providers';
import {
  usePlants,
  useInventoryItem,
  useSubinventoryCodes,
  useSubinventoryInfo,
  useLocators,
  useTransactionItems,
  useCreateInvMgmtTransaction,
  useUpdateRequest,
} from '../../../../hooks';
import { Select, toast } from '@pella/sash-framework';
import { useState } from 'react';
import * as classes from './SubInventoryTransfer.module.css';
import { STATUSES, TRANSACTION_TYPES } from '../../../../data/constants';
import { adjustSubinventoryInfo, formatTransactionsForAPI } from '../../../../data/functions';

/**
 * Drawer for the Sub Inventory Transfer transaction.
 * Opens when a user clicks Complete on an Accepted request with transactionId === 'SUB_INVENTORY_TRANSFER'.
 * @param {Local.ParentTransactionDrawerProps} props - Component props
 */
export const SubInventoryTransferDrawer = ({ open, close, request }) => {
  const { selectedPlant } = useAppContext();
  const { mutate: createInvMgmtTransaction, isPending } = useCreateInvMgmtTransaction();
  const { mutate: updateRequest } = useUpdateRequest();
  const { transactionItems, addQuantity, addFullLot, deleteItem, reset } = useTransactionItems();
  const [selectedSubinventoryFrom, setSelectedSubinventoryFrom] = useState('');
  const [selectedSubinventoryTo, setSelectedSubinventoryTo] = useState('');
  const [selectedLocator, setSelectedLocator] = useState('');

  const { data: plants } = usePlants();

  const plant = plants?.find((p) => p.id === selectedPlant);
  const organizationId = plant?.organizationId ?? 0;

  const inputValue = request?.dynamicInput ?? request?.dropDownListVal1 ?? request?.dropDownListVal2 ?? '';

  const { data: inventoryItems } = useInventoryItem({ item: inputValue, organizationId });

  const inventoryItemId = inventoryItems?.[0]?.inventoryItemId;

  const { data: fromSubinventoryCodes } = useSubinventoryCodes({
    organizationId,
    inventoryItemId: inventoryItemId ?? 0,
    qoh: 'Y',
  });

  const { data: toSubinventoryCodes } = useSubinventoryCodes({
    organizationId,
    inventoryItemId: inventoryItemId ?? 0,
    qoh: 'N',
  });

  const { data: locators, isSuccess: locatorsResolved } = useLocators({
    organizationId,
    inventoryItemId: inventoryItemId ?? 0,
    subinventoryCode: selectedSubinventoryTo,
  });

  const { data: subinventoryInfo } = useSubinventoryInfo({
    organizationId,
    inventoryItemId: inventoryItemId ?? 0,
    subinventoryCode: selectedSubinventoryFrom,
  });

  const uniqueFromSubinventoryCodes = [...new Set(fromSubinventoryCodes?.map((c) => c.subinventoryCode))];
  const uniqueToSubinventoryCodes = [...new Set(toSubinventoryCodes?.map((c) => c.subinventoryCode))];

  const adjustedSubinventoryInfo = adjustSubinventoryInfo(subinventoryInfo || [], transactionItems);

  const selectedLocatorId = locators?.find((l) => l.locator === selectedLocator)?.locatorId ?? null;

  const hasLocators = Array.isArray(locators) && locators.length > 0;
  const noDestinationSelected = !selectedSubinventoryTo || !locatorsResolved || (hasLocators && !selectedLocator);
  const disableComplete = transactionItems.length === 0 || isPending;

  /** Resets form state and closes the drawer */
  const handleClose = () => {
    setSelectedSubinventoryFrom('');
    setSelectedSubinventoryTo('');
    setSelectedLocator('');
    reset();
    close();
  };

  /**
   * Updates the request to Completed status and closes the drawer.
   * @param {(request: Pella.ProductionRequestAPI.Request, onSettled: () => void) => void} completeWithLinkedActionCheck
   * @param {string} errorMessage - Toast message shown if the status update fails.
   */
  const handleCompleteRequest = (completeWithLinkedActionCheck, errorMessage) => {
    completeWithLinkedActionCheck(request, () =>
      updateRequest(
        { id: request.id, status: STATUSES.COMPLETED },
        {
          onSuccess: () => {
            toast.success('Request completed successfully!');
            handleClose();
          },
          onError: () => {
            toast.error(errorMessage);
          },
        },
      ),
    );
  };

  /**
   * Handles form submission
   * @param {(request: Pella.ProductionRequestAPI.Request, onSettled: () => void) => void} completeWithLinkedActionCheck
   */
  const handleSubmit = (completeWithLinkedActionCheck) => {
    const formattedTransactions = formatTransactionsForAPI(
      transactionItems.map((item) => ({ ...item, transactionQuantity: Math.abs(item.transactionQuantity) })),
    );

    createInvMgmtTransaction(
      {
        orgId: organizationId,
        transactions: formattedTransactions,
      },
      {
        onSuccess: () =>
          handleCompleteRequest(
            completeWithLinkedActionCheck,
            'Request completed in WMS but failed to update status. Please refresh.',
          ),
        onError: () => toast.error('Failed to create inventory management transaction. Please try again.'),
      },
    );
  };

  return (
    <TransactionDrawer
      disableSubmit={disableComplete}
      open={open}
      close={handleClose}
      onComplete={handleSubmit}
      request={request}
    >
      {inventoryItems && inventoryItems.length > 0 && (
        <div>
          Selected Org: {organizationId} <br />
          Inventory Item ID: {inventoryItems[0]?.inventoryItemId} <br />
          Requested Item: {inventoryItems[0]?.item} <br />
          Description: {inventoryItems[0]?.description}
        </div>
      )}
      <div className={classes.fieldGrid}>
        <div className={classes.selectWrapper}>
          <Select
            className={classes.selectItem}
            labelText='Sub Inventory From'
            id='SubinventoryFromSelect'
            required
            value={selectedSubinventoryFrom}
            onChange={(e) => setSelectedSubinventoryFrom(e.target.value)}
            data-testid='subinventory-from-select'
          >
            <option value='' key=''>
              Select a Subinventory
            </option>
            {uniqueFromSubinventoryCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </div>
        <div className={classes.selectWrapper}>
          <Select
            className={classes.selectItem}
            labelText='Sub Inventory To'
            id='SubinventoryToSelect'
            required
            value={selectedSubinventoryTo}
            onChange={(e) => {
              setSelectedSubinventoryTo(e.target.value);
              setSelectedLocator('');
            }}
            data-testid='subinventory-to-select'
          >
            <option value='' key=''>
              Select a Subinventory
            </option>
            {uniqueToSubinventoryCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </div>
        {hasLocators && (
          <div className={classes.selectWrapper}>
            <Select
              className={classes.selectItem}
              labelText='Location To'
              id='LocatorToSelect'
              required
              value={selectedLocator}
              onChange={(e) => setSelectedLocator(e.target.value)}
              data-testid='locator-to-select'
            >
              <option value='' key=''>
                Select a Location
              </option>
              {locators?.map((value) => (
                <option key={value.locatorId} value={value.locator}>
                  {value.locator}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>
      {subinventoryInfo && (
        <SubinventoryQtyBox
          subinventoryQtyInfo={adjustedSubinventoryInfo}
          onAddQuantity={(item, qty, index) =>
            addQuantity(
              item,
              qty,
              index,
              TRANSACTION_TYPES.SUB_INVENTORY_TRANSFER,
              organizationId,
              undefined,
              selectedSubinventoryTo,
              selectedLocatorId,
            )
          }
          onAddFullLot={(item, qty, index) =>
            addFullLot(
              item,
              qty,
              index,
              TRANSACTION_TYPES.SUB_INVENTORY_TRANSFER,
              organizationId,
              undefined,
              selectedSubinventoryTo,
              selectedLocatorId,
            )
          }
          transactionType={TRANSACTION_TYPES.SUB_INVENTORY_TRANSFER}
          orgId={organizationId}
          disableAddQuantity={noDestinationSelected}
          disableAddFullLot={noDestinationSelected}
        />
      )}
      <TransactionItemsList items={transactionItems} onDelete={deleteItem} />
    </TransactionDrawer>
  );
};
